import { CATEGORY_LABELS, MONTH_NAMES } from "./format";

const BASE = "https://willnotbeforgotten.org";

const ORG = {
  "@type": "Organization",
  "@id": `${BASE}/#organization`,
  name: "Will Not Be Forgotten",
  url: BASE,
  logo: `${BASE}/wnbf-logo.png`,
  description:
    "An independent civic-memory project preserving the U.S. holidays, observances, heritage months, and historic milestones that shape a fuller American story.",
};

const WEBSITE = {
  "@type": "WebSite",
  "@id": `${BASE}/#website`,
  url: BASE,
  name: "Will Not Be Forgotten",
  description: "A living archive of U.S. holidays, observances, and historic milestones.",
  inLanguage: "en-US",
  publisher: { "@id": `${BASE}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${BASE}/?q={search_term_string}#explorer`,
    },
    "query-input": "required name=search_term_string",
  },
};

/** Schema graph for the home page. */
export const homePageJsonLd = () => ({
  "@context": "https://schema.org",
  "@graph": [ORG, WEBSITE],
});

// The archive itself launched in early 2026; use that as the static
// publication date for every Article. The historical year/date for the
// underlying observance lives in Event.startDate instead, where it
// belongs semantically. dateModified is set to today so freshness signals
// stay honest.
const ARCHIVE_PUBLISHED_AT = "2026-02-01";
const todayIso = () => new Date().toISOString().slice(0, 10);

const eventStartDate = (entry) => {
  if (!entry?.month || !entry?.day) return null;
  const year = entry.year_established || new Date().getUTCFullYear();
  const m = String(entry.month).padStart(2, "0");
  const d = String(entry.day).padStart(2, "0");
  return `${year}-${m}-${d}`;
};

const dateLabel = (entry) => {
  if (entry.month && entry.day) return `${MONTH_NAMES[entry.month]} ${entry.day}`;
  if (entry.month) return MONTH_NAMES[entry.month];
  return entry.date_label || "";
};

/** Schema graph for a single /date/:id detail page. */
export const dateEntryJsonLd = (entry) => {
  if (!entry) return null;
  const url = `${BASE}/date/${entry.id}`;
  const image = `${BASE}/api/og/${entry.id}.png`;
  const keywords = [
    ...(entry.categories || []).map((c) => CATEGORY_LABELS[c] || c),
    ...(entry.tags || []),
    entry.recognition_status,
  ].filter(Boolean);

  const article = {
    "@type": "Article",
    "@id": `${url}#article`,
    headline: entry.title,
    name: entry.title,
    description: entry.short_description,
    articleBody: entry.full_history || entry.significance || entry.short_description,
    image: { "@type": "ImageObject", url: image, width: 1200, height: 630 },
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: "en-US",
    isPartOf: { "@id": `${BASE}/#website` },
    publisher: { "@id": `${BASE}/#organization` },
    author: { "@id": `${BASE}/#organization` },
    datePublished: ARCHIVE_PUBLISHED_AT,
    dateModified: todayIso(),
    keywords: keywords.join(", "),
    about: keywords.slice(0, 6).map((k) => ({ "@type": "Thing", name: k })),
  };
  if (entry.people && entry.people.length) {
    article.mentions = entry.people.map((p) => ({ "@type": "Person", name: p }));
  }

  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Archive", item: BASE },
      {
        "@type": "ListItem",
        position: 2,
        name: dateLabel(entry) || "Entry",
        item: url,
      },
      { "@type": "ListItem", position: 3, name: entry.title, item: url },
    ],
  };

  const graph = [article, breadcrumb];

  // If the entry is a date-bound observance, also expose it as an Event so it
  // becomes eligible for Google's event/holiday rich-result rendering.
  const startDate = eventStartDate(entry);
  if (startDate) {
    graph.push({
      "@type": "Event",
      "@id": `${url}#event`,
      name: entry.title,
      description: entry.short_description,
      url,
      image,
      startDate,
      endDate: startDate,
      eventAttendanceMode: "https://schema.org/MixedEventAttendanceMode",
      eventStatus: "https://schema.org/EventScheduled",
      location: {
        "@type": "Country",
        name: "United States",
      },
      organizer: { "@id": `${BASE}/#organization` },
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
};

/** Schema graph for a /category/:slug landing page. */
export const categoryPageJsonLd = (slug, label, description, entries) => {
  const url = `${BASE}/category/${slug}`;
  const itemList = {
    "@type": "ItemList",
    "@id": `${url}#list`,
    name: `${label} — U.S. observances & historic milestones`,
    numberOfItems: entries.length,
    itemListElement: entries.map((e, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${BASE}/date/${e.id}`,
      name: e.title,
    })),
  };
  const collection = {
    "@type": "CollectionPage",
    "@id": `${url}#webpage`,
    url,
    name: `${label} | Will Not Be Forgotten`,
    description,
    isPartOf: { "@id": `${BASE}/#website` },
    about: { "@type": "Thing", name: label },
    mainEntity: { "@id": `${url}#list` },
    inLanguage: "en-US",
  };
  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Archive", item: BASE },
      { "@type": "ListItem", position: 2, name: label, item: url },
    ],
  };
  return { "@context": "https://schema.org", "@graph": [collection, itemList, breadcrumb] };
};
